import { UI } from '../nextpage';

export class Dropdown {
    constructor(private NP: UI) {
        // Dropdown component
        // Click event that is applied to buttons in the dropdown
        this.NP.registerEvent("dropdownButtonClick", (e: Event) => {
            e.stopImmediatePropagation();
            const clicked = e.target as HTMLButtonElement;
            const pressed = clicked.classList.contains("aria-pressed");
            this.NP.blurPromise(clicked).then((ddButton: HTMLButtonElement) => {
                // If the button was already pressed do nothing.
                if (pressed === true) {
                    return;
                }
                // Otherwise add this button and its list to the blurAttributes and open the list.
                const listbox = ddButton.parentNode.querySelector("ul[role='listbox']") as HTMLUListElement;
                if (listbox === null) {
                    return;
                }
                this.NP.properties.blurAria.push(
                    {selector: ddButton, class: 'pressed', active: false},
                    {selector: listbox, class: 'expanded', active: false}
                );
                this.NP.toggleAriaState(ddButton, "pressed", !pressed);
                this.NP.toggleAriaState(listbox, "expanded", !pressed);
            }).catch(rejection => console.log(`dropdownButtonClick event broken promise: ${rejection}`));
        });

        // Click event that is applied to options in the dropdown list
        this.NP.registerEvent("dropdownOptionClick", (e: Event) => {
            e.stopImmediatePropagation();
            /*
                When we click any option in the list, we should call the BlurPromise to ensure that this dropdown
                and any other elements are blurred/closed properly.
            */
            const clicked = e.target as HTMLLIElement;
            this.NP.blurPromise(clicked).then((ddOption: HTMLLIElement) => {
                let parentContainer = ddOption.parentElement;
                const inMulti = parentContainer.classList.contains("multi-select");
                const optionIndex = [].indexOf.call(ddOption.parentElement.children, ddOption);
                let foundParent = false;
                while(foundParent === false && !inMulti) {
                    if (parentContainer.classList.contains("dropdown")) {
                        foundParent = true;
                        continue;
                    }
                    parentContainer = parentContainer.parentElement;
                }

                // Find the connected <option> if this was generated from a <select> element.
                // Also find the parent <select> of that <option> if applicable.
                const dropdownNextSibling = parentContainer.nextElementSibling as HTMLSelectElement;
                let connectedSelect: HTMLSelectElement = null;
                let connectedOption: HTMLOptionElement = null;
                if (optionIndex !== -1 && dropdownNextSibling !== null && dropdownNextSibling.nodeName === "SELECT") {
                    connectedSelect = dropdownNextSibling;
                    connectedOption = connectedSelect.options[optionIndex];
                }

                // If this is in a multi-select list we only toggle this option's selected attribute.
                if (inMulti) {
                    const shouldSelect = !ddOption.classList.contains("aria-selected");
                    this.NP.toggleAriaState(ddOption, "selected", shouldSelect);

                    // If no connected form elements are found do not continue.
                    if (connectedSelect === null || connectedOption == null) { return; }
                    // If found set the <option> to selected and trigger the change event on the <select> element.
                    connectedOption.selected = shouldSelect;
                    connectedSelect.dispatchEvent(new Event("change"));
                    return;
                }

                // If this is in a regular select list set this option to selected and all siblings to not selected.
                ddOption.parentElement.querySelectorAll("li[role='option']").forEach((listItem: HTMLLIElement, i: number) => {
                    this.NP.toggleAriaState(listItem, "selected", i === optionIndex);
                });
                parentContainer.querySelectorAll("button").forEach((button: HTMLButtonElement) => {
                    button.innerHTML = ddOption.innerHTML;
                });
                if (connectedSelect === null || connectedOption == null) { return; }
                // If connected form elements are found set them and trigger the change event on the <select> element.
                connectedSelect.options[connectedSelect.options.selectedIndex].selected = false;
                connectedOption.selected = true;
                connectedSelect.dispatchEvent(new Event("change"));
            }).catch(rejection => console.log(`dropdownOptionClick event broken promise: ${rejection}`));
        });

        this.NP.components.dropdown = (selector: string | Element | NodeListOf<Element> | Array<Element>): void => {
            this.NP.getComponentSelector(selector).forEach((dropdown: HTMLDivElement) => {
                dropdown.classList.toggle("layout-check", true);

                // If no valid elements with aria attributes are found skip this element.
                const dropdownList = dropdown.querySelector("ul[role='listbox']") as HTMLUListElement;
                if (dropdownList === null) {
                    return;
                }
                const dropdownOptions = dropdownList.querySelectorAll("li[role='option']");
                if (dropdownOptions.length === 0) {
                    return;
                }

                let optionWidth = 0; // Placeholder for the option width to be used with the auto-size option.
                const isMulti = dropdown.classList.contains("multi-select"); // Whether this is a multi-select style box in a form
                const isAutoSized = dropdown.classList.contains("auto-size"); // Whether this will be auto-sized

                // With each button in the generated select, check the aria-pressed, then blurPromise, then open.
                dropdown.querySelectorAll("button").forEach((button) => {
                    button.addEventListener("click", (e: Event) => this.NP.events.dropdownButtonClick(e));
                });

                // With each dropdown option, bind the appropriate event and then check the size if we're using auto-size.
                dropdownOptions.forEach((opt: HTMLLIElement) => {
                    opt.addEventListener("click", (e: Event) => this.NP.events.dropdownOptionClick(e));
                    if (isMulti || !isAutoSized) {
                        return;
                    }
                    // If this option width is found to be the largest set the optionWidth to this width.
                    const thisWidth = opt.clientWidth;
                    if (thisWidth <= optionWidth) {
                        return;
                    }
                    optionWidth = thisWidth;
                });

                // If this is a multi-select or auto-sizing is not enabled set the CSS items and remove layout-check.
                if (isMulti || !isAutoSized) {
                    if (!isMulti) {
                        this.NP.toggleAriaState(dropdownList, "expanded", false);
                    }
                    dropdown.classList.toggle("layout-check", false);
                    return;
                }
            
                // If this is not a multi-select and auto-sizing is used, set the width of all options to the optionWidth
                dropdown.querySelectorAll("button").forEach((button: HTMLButtonElement) => {
                    button.style.width = optionWidth + 20 + "px";
                });
                dropdown.style.width = optionWidth + 20 + "px";
                this.NP.toggleAriaState(dropdownList, "expanded", false);
                dropdownList.style.alignItems = "stretch";
                dropdown.classList.toggle("layout-check", false);
            });
        }
    }
}